import LonghouseModal from "@/components/modals/LonghouseModal";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    CheckCircle,
    Clock,
    GraduationCap,
    GripVertical,
    Mail,
    Search,
    Trash2,
    UserPlus,
    XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STREAM_TEAM_REQUESTS_QUERY = gql`
    query StreamTeamRequests($status: String) {
        streamTeamRequests(status: $status) {
            id
            player {
                id
                name
            }
            message
            status
            createdAt
            reviewedAt
            isAcademyApplication
            reviewedBy {
                id
                name
            }
            rejectionReason
        }
    }
`;

const STREAM_TEAM_INVITES_QUERY = gql`
    query StreamTeamInvites($status: String) {
        streamTeamInvites(status: $status) {
            id
            player {
                id
                name
            }
            invitedBy {
                id
                name
            }
            message
            status
            createdAt
            respondedAt
        }
    }
`;

const STREAM_SETTINGS_QUERY = gql`
    query StreamSettings {
        streamSettings {
            id
            acceptingApplications
            acceptingAcademyApplications
        }
        featureFlags {
            streamTeamChannelPointRewardEnabled
        }
    }
`;

const STREAM_TEAM_MEMBERS_QUERY = gql`
    query StreamTeamMembers($activeOnly: Boolean, $academyOnly: Boolean) {
        streamTeamMembers(activeOnly: $activeOnly, academyOnly: $academyOnly) {
            id
            player {
                id
                name
                clan {
                    tag
                    color {
                        hex
                    }
                }
            }
            bio
            twitchUsername
            isAcademy
            isActive
            joinedAt
            hasTwitchAuth
            twitchAuth {
                streamTeamRewardEnabled
            }
            canCreateEvents
            displayOrder
            totalRavenguardPoints
            runestones {
                imageUrl
            }
        }
    }
`;

const APPROVE_REQUEST_MUTATION = gql`
    mutation ApproveStreamTeamRequest($requestId: UUID!) {
        approveStreamTeamRequest(requestId: $requestId) {
            success
            streamTeamMember {
                id
            }
        }
    }
`;

const REJECT_REQUEST_MUTATION = gql`
    mutation RejectStreamTeamRequest($requestId: UUID!, $reason: String) {
        rejectStreamTeamRequest(requestId: $requestId, reason: $reason) {
            success
        }
    }
`;

const SEND_REQUEST_TO_ACADEMY_MUTATION = gql`
    mutation SendStreamTeamRequestToAcademy($requestId: UUID!) {
        sendStreamTeamRequestToAcademy(requestId: $requestId) {
            success
            streamTeamMember {
                id
            }
        }
    }
`;

const PROMOTE_ACADEMY_MEMBER_MUTATION = gql`
    mutation PromoteAcademyMemberToStreamTeam($memberId: UUID!) {
        promoteAcademyMemberToStreamTeam(memberId: $memberId) {
            success
            streamTeamMember {
                id
            }
        }
    }
`;

const INVITE_TO_STREAM_TEAM_MUTATION = gql`
    mutation InviteToStreamTeam($playerId: UUID!, $message: String) {
        inviteToStreamTeam(playerId: $playerId, message: $message) {
            success
            invite {
                id
            }
        }
    }
`;

const TOGGLE_APPLICATIONS_MUTATION = gql`
    mutation ToggleStreamTeamApplications(
        $acceptingApplications: Boolean
        $acceptingAcademyApplications: Boolean
    ) {
        toggleStreamTeamApplications(
            acceptingApplications: $acceptingApplications
            acceptingAcademyApplications: $acceptingAcademyApplications
        ) {
            success
            acceptingApplications
            acceptingAcademyApplications
        }
    }
`;

const CANCEL_INVITE_MUTATION = gql`
    mutation CancelStreamTeamInvite($inviteId: UUID!) {
        cancelStreamTeamInvite(inviteId: $inviteId) {
            success
        }
    }
`;

const REMOVE_MEMBER_MUTATION = gql`
    mutation RemoveStreamTeamMember($memberId: UUID!) {
        removeStreamTeamMember(memberId: $memberId) {
            success
        }
    }
`;

const UPDATE_MEMBER_ORDER_MUTATION = gql`
    mutation UpdateStreamTeamMemberOrder($memberOrders: [MemberOrderInput!]!) {
        updateStreamTeamMemberOrder(memberOrders: $memberOrders) {
            success
        }
    }
`;

const TOGGLE_EVENT_PERMISSION_MUTATION = gql`
    mutation ToggleStreamTeamMemberEventPermission(
        $memberId: UUID!
        $canCreateEvents: Boolean!
    ) {
        toggleStreamTeamMemberEventPermission(
            memberId: $memberId
            canCreateEvents: $canCreateEvents
        ) {
            success
            streamTeamMember {
                id
                canCreateEvents
            }
        }
    }
`;

const SYNC_STREAM_TEAM_CHANNEL_POINT_REWARDS_FROM_SETTINGS_MUTATION = gql`
    mutation SyncStreamTeamChannelPointRewardsFromSettings($memberId: UUID) {
        syncStreamTeamChannelPointRewardsFromSettings(memberId: $memberId) {
            success
            updatedCount
            failedCount
        }
    }
`;

const SEARCH_PLAYERS_QUERY = gql`
    query SearchPlayers($searchQuery: String, $limit: Int) {
        searchPlayers(searchQuery: $searchQuery, limit: $limit) {
            id
            name
            clan {
                tag
            }
        }
    }
`;

interface SelectedPlayer {
    id: string;
    name: string;
    clan?: { tag: string };
}

interface SortableMemberRowProps {
    member: any;
    onRemove: (id: string, name: string) => void;
    onToggleEventPermission: (
        id: string,
        name: string,
        currentValue: boolean,
    ) => void;
    onViewRunestone: (imageUrl: string, playerName: string) => void;
}

function SortableMemberRow({
    member,
    onRemove,
    onToggleEventPermission,
    onViewRunestone,
}: SortableMemberRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: member.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
        >
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                    <button
                        {...attributes}
                        {...listeners}
                        className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:hover:text-slate-300"
                    >
                        <GripVertical className="h-5 w-5" />
                    </button>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                        <Link
                            to={`/longhouse/stream-team/${member.id}`}
                            className="text-blue-700 underline-offset-2 hover:underline dark:text-blue-300"
                        >
                            {member.player.name}
                        </Link>
                    </span>
                    {member.player.clan && (
                        <span
                            className="rounded px-2 py-0.5 text-xs font-semibold text-white"
                            style={{
                                backgroundColor:
                                    member.player.clan.color?.hex || "#000",
                            }}
                        >
                            {member.player.clan.tag}
                        </span>
                    )}
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                    <span className="text-slate-700 dark:text-slate-300">
                        {member.twitchUsername || "—"}
                    </span>
                    {member.hasTwitchAuth && (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        member.twitchAuth?.streamTeamRewardEnabled
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    }`}
                >
                    {member.twitchAuth?.streamTeamRewardEnabled
                        ? "Enabled"
                        : "Not Enabled"}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className="font-medium text-slate-900 dark:text-slate-100">
                    {(member.totalRavenguardPoints ?? 0).toLocaleString()}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        member.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    }`}
                >
                    {member.isActive ? "Active" : "Inactive"}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <button
                    onClick={() =>
                        onToggleEventPermission(
                            member.id,
                            member.player.name,
                            member.canCreateEvents,
                        )
                    }
                    className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
                    style={{
                        backgroundColor: member.canCreateEvents
                            ? "#3b82f6"
                            : "#6b7280",
                    }}
                    title={
                        member.canCreateEvents
                            ? "Click to revoke event creation permission"
                            : "Click to grant event creation permission"
                    }
                    role="switch"
                    aria-checked={member.canCreateEvents}
                >
                    <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            member.canCreateEvents
                                ? "translate-x-5"
                                : "translate-x-0"
                        }`}
                    />
                </button>
            </td>
            <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                {new Date(member.joinedAt).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                {member.displayOrder || "—"}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                {member.runestones && member.runestones.length > 0 ? (
                    <button
                        onClick={() =>
                            onViewRunestone(
                                member.runestones[0].imageUrl,
                                member.player.name,
                            )
                        }
                        className="transition-opacity hover:opacity-75"
                        title="View Runestone"
                    >
                        <img
                            src={member.runestones[0].imageUrl}
                            alt="Runestone"
                            className="h-10 w-10 object-contain"
                        />
                    </button>
                ) : (
                    <span className="text-slate-400 dark:text-slate-600">
                        —
                    </span>
                )}
            </td>
            <td className="px-6 py-4 text-right whitespace-nowrap">
                <button
                    onClick={() => onRemove(member.id, member.player.name)}
                    className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                    <Trash2 className="h-4 w-4" />
                    Remove
                </button>
            </td>
        </tr>
    );
}

interface SortableAcademyMemberRowProps {
    member: any;
    onRemove: (id: string, name: string) => void;
    onPromote: (id: string, name: string) => void;
    onViewRunestone: (imageUrl: string, playerName: string) => void;
}

function SortableAcademyMemberRow({
    member,
    onRemove,
    onPromote,
    onViewRunestone,
}: SortableAcademyMemberRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: member.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
        >
            <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                    <button
                        {...attributes}
                        {...listeners}
                        className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:hover:text-slate-300"
                    >
                        <GripVertical className="h-5 w-5" />
                    </button>
                    <Link
                        to={`/longhouse/stream-team/${member.id}`}
                        className="font-medium text-blue-700 underline-offset-2 hover:underline dark:text-blue-300"
                    >
                        {member.player.name}
                    </Link>
                </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-slate-700 dark:text-slate-300">
                {member.twitchUsername || "—"}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        member.twitchAuth?.streamTeamRewardEnabled
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"
                            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    }`}
                >
                    {member.twitchAuth?.streamTeamRewardEnabled
                        ? "Enabled"
                        : "Not Enabled"}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className="font-medium text-slate-900 dark:text-slate-100">
                    {(member.totalRavenguardPoints ?? 0).toLocaleString()}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        member.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    }`}
                >
                    {member.isActive ? "Active" : "Inactive"}
                </span>
            </td>
            <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                {new Date(member.joinedAt).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 text-sm whitespace-nowrap text-slate-600 dark:text-slate-400">
                {member.displayOrder || "—"}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                {member.runestones && member.runestones.length > 0 ? (
                    <button
                        onClick={() =>
                            onViewRunestone(
                                member.runestones[0].imageUrl,
                                member.player.name,
                            )
                        }
                        className="transition-opacity hover:opacity-75"
                        title="View Runestone"
                    >
                        <img
                            src={member.runestones[0].imageUrl}
                            alt="Runestone"
                            className="h-10 w-10 object-contain"
                        />
                    </button>
                ) : (
                    <span className="text-slate-400 dark:text-slate-600">
                        —
                    </span>
                )}
            </td>
            <td className="px-6 py-4 text-right whitespace-nowrap">
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => onPromote(member.id, member.player.name)}
                        className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700"
                    >
                        <GraduationCap className="h-4 w-4" />
                        Promote to Stream Team
                    </button>
                    <button
                        onClick={() => onRemove(member.id, member.player.name)}
                        className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
                    >
                        <Trash2 className="h-4 w-4" />
                        Remove
                    </button>
                </div>
            </td>
        </tr>
    );
}

export default function StreamTeamPage() {
    const [activeTab, setActiveTab] = useState<
        "members" | "academy" | "requests" | "invites"
    >("members");
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteMessage, setInviteMessage] = useState("");
    const [selectedPlayers, setSelectedPlayers] = useState<SelectedPlayer[]>(
        [],
    );
    const [playerSearch, setPlayerSearch] = useState("");
    const [rejectReason, setRejectReason] = useState("");
    const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(
        null,
    );
    const [members, setMembers] = useState<any[]>([]);
    const [academyMembers, setAcademyMembers] = useState<any[]>([]);

    // Runestone zoom modal
    const [runeModal, setRuneModal] = useState<{
        open: boolean;
        imageUrl: string;
        playerName: string;
    }>({ open: false, imageUrl: "", playerName: "" });

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState<{
        title: string;
        message: string;
        type: "alert" | "confirm" | "error" | "success" | "info";
        onConfirm?: () => void;
    }>({
        title: "",
        message: "",
        type: "info",
    });

    const showModal = (
        title: string,
        message: string,
        type: "alert" | "confirm" | "error" | "success" | "info" = "info",
        onConfirm?: () => void,
    ) => {
        setModalConfig({ title, message, type, onConfirm });
        setModalOpen(true);
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const {
        data: membersData,
        loading: membersLoading,
        refetch: refetchMembers,
    } = useQuery(STREAM_TEAM_MEMBERS_QUERY, {
        variables: { activeOnly: false, academyOnly: false },
    });

    const {
        data: academyMembersData,
        loading: academyMembersLoading,
        refetch: refetchAcademyMembers,
    } = useQuery(STREAM_TEAM_MEMBERS_QUERY, {
        variables: { activeOnly: false, academyOnly: true },
    });

    const {
        data: requestsData,
        loading: requestsLoading,
        refetch: refetchRequests,
    } = useQuery(STREAM_TEAM_REQUESTS_QUERY, {
        variables: { status: "pending" },
    });

    const {
        data: invitesData,
        loading: invitesLoading,
        refetch: refetchInvites,
    } = useQuery(STREAM_TEAM_INVITES_QUERY, {
        variables: { status: "pending" },
    });

    const { data: settingsData, refetch: refetchSettings } = useQuery(
        STREAM_SETTINGS_QUERY,
    );

    const { data: searchData } = useQuery(SEARCH_PLAYERS_QUERY, {
        variables: { searchQuery: playerSearch, limit: 10 },
        skip: !playerSearch || playerSearch.length < 2,
    });

    const [approveRequest] = useMutation(APPROVE_REQUEST_MUTATION);
    const [rejectRequest] = useMutation(REJECT_REQUEST_MUTATION);
    const [sendRequestToAcademy] = useMutation(
        SEND_REQUEST_TO_ACADEMY_MUTATION,
    );
    const [promoteAcademyMember] = useMutation(PROMOTE_ACADEMY_MEMBER_MUTATION);
    const [inviteToStreamTeam] = useMutation(INVITE_TO_STREAM_TEAM_MUTATION);
    const [toggleApplications] = useMutation(TOGGLE_APPLICATIONS_MUTATION);
    const [cancelInvite] = useMutation(CANCEL_INVITE_MUTATION);
    const [removeMember] = useMutation(REMOVE_MEMBER_MUTATION);
    const [updateMemberOrder] = useMutation(UPDATE_MEMBER_ORDER_MUTATION);
    const [toggleEventPermission] = useMutation(
        TOGGLE_EVENT_PERMISSION_MUTATION,
    );
    const [syncRewardsFromSettings] = useMutation(
        SYNC_STREAM_TEAM_CHANNEL_POINT_REWARDS_FROM_SETTINGS_MUTATION,
    );

    // Sync members state with query data
    useEffect(() => {
        if (membersData?.streamTeamMembers) {
            setMembers(membersData.streamTeamMembers);
        }
    }, [membersData]);

    useEffect(() => {
        if (academyMembersData?.streamTeamMembers) {
            setAcademyMembers(academyMembersData.streamTeamMembers);
        }
    }, [academyMembersData]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = members.findIndex((m) => m.id === active.id);
        const newIndex = members.findIndex((m) => m.id === over.id);

        const newMembers = arrayMove(members, oldIndex, newIndex);

        // Update local state immediately for smooth UX
        setMembers(newMembers);

        // Prepare the member orders with new display_order values
        const memberOrders = newMembers.map((member, index) => ({
            memberId: member.id,
            displayOrder: index + 1,
        }));

        try {
            await updateMemberOrder({
                variables: { memberOrders },
            });
            // Refetch to ensure UI is in sync with backend
            refetchMembers();
        } catch (error) {
            console.error("Error updating member order:", error);
            showModal(
                "Error",
                "Failed to update order. Please try again.",
                "error",
            );
            // Revert on error
            if (membersData?.streamTeamMembers) {
                setMembers(membersData.streamTeamMembers);
            }
        }
    };

    const handleAcademyDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = academyMembers.findIndex((m) => m.id === active.id);
        const newIndex = academyMembers.findIndex((m) => m.id === over.id);

        const newAcademyMembers = arrayMove(academyMembers, oldIndex, newIndex);

        // Update local state immediately for smooth UX
        setAcademyMembers(newAcademyMembers);

        const memberOrders = newAcademyMembers.map((member, index) => ({
            memberId: member.id,
            displayOrder: index + 1,
        }));

        try {
            await updateMemberOrder({
                variables: { memberOrders },
            });
            refetchAcademyMembers();
        } catch (error) {
            console.error("Error updating academy member order:", error);
            showModal(
                "Error",
                "Failed to update order. Please try again.",
                "error",
            );
            // Revert on error
            if (academyMembersData?.streamTeamMembers) {
                setAcademyMembers(academyMembersData.streamTeamMembers);
            }
        }
    };

    const handleApproveRequest = async (requestId: string) => {
        showModal(
            "Confirm Approval",
            "Are you sure you want to approve this request?",
            "confirm",
            async () => {
                try {
                    await approveRequest({ variables: { requestId } });
                    refetchRequests();
                    refetchMembers();
                } catch (error) {
                    console.error("Error approving request:", error);
                    showModal(
                        "Error",
                        "Failed to approve request. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleRejectRequest = async (requestId: string) => {
        try {
            await rejectRequest({
                variables: { requestId, reason: rejectReason },
            });
            setRejectingRequestId(null);
            setRejectReason("");
            refetchRequests();
        } catch (error) {
            console.error("Error rejecting request:", error);
            showModal(
                "Error",
                "Failed to reject request. Please try again.",
                "error",
            );
        }
    };

    const handleSendRequestToAcademy = async (requestId: string) => {
        showModal(
            "Confirm Academy Move",
            "Send this applicant to Stream Team Academy?",
            "confirm",
            async () => {
                try {
                    await sendRequestToAcademy({ variables: { requestId } });
                    refetchRequests();
                    refetchAcademyMembers();
                } catch (error) {
                    console.error("Error sending request to academy:", error);
                    showModal(
                        "Error",
                        "Failed to move applicant to academy. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleInvitePlayer = async () => {
        if (selectedPlayers.length === 0) {
            showModal(
                "Validation Error",
                "Please select at least one player to invite.",
                "error",
            );
            return;
        }

        try {
            // Send invites for all selected players
            await Promise.all(
                selectedPlayers.map((player) =>
                    inviteToStreamTeam({
                        variables: {
                            playerId: player.id,
                            message: inviteMessage,
                        },
                    }),
                ),
            );
            setShowInviteModal(false);
            setInviteMessage("");
            setSelectedPlayers([]);
            setPlayerSearch("");
            refetchInvites();
            showModal(
                "Success",
                `Invitation${selectedPlayers.length > 1 ? "s" : ""} sent successfully!`,
                "success",
            );
        } catch (error) {
            console.error("Error inviting player:", error);
            showModal(
                "Error",
                "Failed to send invitation. Please try again.",
                "error",
            );
        }
    };

    const handleToggleApplications = async (academy: boolean) => {
        const settings = settingsData?.streamSettings;
        const currentlyAccepting = academy
            ? (settings?.acceptingAcademyApplications ?? true)
            : (settings?.acceptingApplications ?? true);
        const action = currentlyAccepting ? "close" : "open";
        const label = academy ? "VRX Academy" : "RAVENGUARD";

        showModal(
            "Confirm Action",
            `Are you sure you want to ${action} ${label} applications?`,
            "confirm",
            async () => {
                try {
                    await toggleApplications({
                        variables: academy
                            ? {
                                  acceptingAcademyApplications:
                                      !currentlyAccepting,
                              }
                            : { acceptingApplications: !currentlyAccepting },
                    });
                    refetchSettings();
                } catch (error) {
                    console.error("Error toggling applications:", error);
                    showModal(
                        "Error",
                        "Failed to update application status. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleCancelInvite = async (inviteId: string, playerName: string) => {
        showModal(
            "Confirm Cancellation",
            `Are you sure you want to cancel the invitation for ${playerName}?`,
            "confirm",
            async () => {
                try {
                    await cancelInvite({
                        variables: { inviteId },
                        refetchQueries: ["MyAccount", "StreamTeamInvites"],
                    });
                    refetchInvites();
                    showModal(
                        "Success",
                        "Invitation cancelled successfully.",
                        "success",
                    );
                } catch (error) {
                    console.error("Error canceling invite:", error);
                    showModal(
                        "Error",
                        "Failed to cancel invitation. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleRemoveMember = async (memberId: string, playerName: string) => {
        showModal(
            "Confirm Removal",
            `Are you sure you want to remove ${playerName} from the stream team?`,
            "confirm",
            async () => {
                try {
                    await removeMember({ variables: { memberId } });
                    refetchMembers();
                } catch (error) {
                    console.error("Error removing member:", error);
                    showModal(
                        "Error",
                        "Failed to remove member. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handlePromoteAcademyMember = async (
        memberId: string,
        playerName: string,
    ) => {
        showModal(
            "Confirm Promotion",
            `Promote ${playerName} to full stream team?`,
            "confirm",
            async () => {
                try {
                    await promoteAcademyMember({ variables: { memberId } });
                    refetchMembers();
                    refetchAcademyMembers();
                } catch (error) {
                    console.error("Error promoting academy member:", error);
                    showModal(
                        "Error",
                        "Failed to promote academy member. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleToggleEventPermission = async (
        memberId: string,
        playerName: string,
        currentValue: boolean,
    ) => {
        const action = currentValue ? "revoke" : "grant";
        showModal(
            "Confirm Permission Change",
            `Are you sure you want to ${action} event creation permission for ${playerName}?`,
            "confirm",
            async () => {
                try {
                    await toggleEventPermission({
                        variables: {
                            memberId,
                            canCreateEvents: !currentValue,
                        },
                    });
                    refetchMembers();
                    showModal(
                        "Success",
                        `Event creation permission ${action}ed successfully.`,
                        "success",
                    );
                } catch (error) {
                    console.error("Error toggling event permission:", error);
                    showModal(
                        "Error",
                        "Failed to update permission. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const handleSyncRewardsFromSettings = async () => {
        showModal(
            "Confirm Reward Sync",
            "Sync stream-team channel point rewards using current stream settings?",
            "confirm",
            async () => {
                try {
                    const result = await syncRewardsFromSettings();
                    const payload =
                        result.data
                            ?.syncStreamTeamChannelPointRewardsFromSettings;
                    showModal(
                        "Sync Complete",
                        `Updated ${payload?.updatedCount ?? 0} member rewards. Failed: ${payload?.failedCount ?? 0}.`,
                        "success",
                    );
                    refetchMembers();
                } catch (error) {
                    console.error("Error syncing stream-team rewards:", error);
                    showModal(
                        "Error",
                        "Failed to sync rewards. Please try again.",
                        "error",
                    );
                }
            },
        );
    };

    const requests = requestsData?.streamTeamRequests || [];
    const invites = invitesData?.streamTeamInvites || [];
    const searchResults = searchData?.searchPlayers || [];
    const acceptingApplications =
        settingsData?.streamSettings?.acceptingApplications ?? true;
    const acceptingAcademyApplications =
        settingsData?.streamSettings?.acceptingAcademyApplications ?? true;
    const selectedPlayerIds = selectedPlayers.map((p) => p.id);

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* Page Header */}
            <div className="mb-6 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                            Stream Team Management
                        </h1>
                        <p className="mt-2 text-slate-600 dark:text-slate-400">
                            Manage stream team members and review join requests
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {settingsData?.featureFlags
                            ?.streamTeamChannelPointRewardEnabled && (
                            <button
                                onClick={handleSyncRewardsFromSettings}
                                className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                            >
                                Sync Team Rewards
                            </button>
                        )}
                        <button
                            onClick={() => handleToggleApplications(false)}
                            className={`flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors ${
                                acceptingApplications
                                    ? "bg-red-600 hover:bg-red-700"
                                    : "bg-green-600 hover:bg-green-700"
                            }`}
                        >
                            {acceptingApplications
                                ? "Close RAVENGUARD Applications"
                                : "Open RAVENGUARD Applications"}
                        </button>
                        <button
                            onClick={() => handleToggleApplications(true)}
                            className={`flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors ${
                                acceptingAcademyApplications
                                    ? "bg-red-600 hover:bg-red-700"
                                    : "bg-green-600 hover:bg-green-700"
                            }`}
                        >
                            <GraduationCap className="h-5 w-5" />
                            {acceptingAcademyApplications
                                ? "Close Academy Applications"
                                : "Open Academy Applications"}
                        </button>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="bg-brand hover:bg-brand/90 flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors"
                        >
                            <UserPlus className="h-5 w-5" />
                            Invite Player
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-6 rounded-t-2xl border border-b-0 border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900">
                <div className="flex">
                    <button
                        onClick={() => setActiveTab("members")}
                        className={`flex-1 border-b-2 px-6 py-4 font-semibold transition-colors ${
                            activeTab === "members"
                                ? "border-brand text-brand dark:border-brand dark:text-brand"
                                : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                    >
                        Members ({members.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("academy")}
                        className={`flex-1 border-b-2 px-6 py-4 font-semibold transition-colors ${
                            activeTab === "academy"
                                ? "border-brand text-brand dark:border-brand dark:text-brand"
                                : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                    >
                        Academy Members ({academyMembers.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("requests")}
                        className={`flex-1 border-b-2 px-6 py-4 font-semibold transition-colors ${
                            activeTab === "requests"
                                ? "border-brand text-brand dark:border-brand dark:text-brand"
                                : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                    >
                        Requests ({requests.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("invites")}
                        className={`flex-1 border-b-2 px-6 py-4 font-semibold transition-colors ${
                            activeTab === "invites"
                                ? "border-brand text-brand dark:border-brand dark:text-brand"
                                : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                    >
                        Invites ({invites.length})
                    </button>
                </div>
            </div>

            {/* Members Tab */}
            {activeTab === "members" && (
                <div className="rounded-t-none rounded-b-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    {membersLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                        </div>
                    ) : members.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-slate-600 dark:text-slate-400">
                                No stream team members yet
                            </p>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Player
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Twitch
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Reward
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Total RP
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Events
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Joined
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Display Order
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Runestone
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                        <SortableContext
                                            items={members.map((m) => m.id)}
                                            strategy={
                                                verticalListSortingStrategy
                                            }
                                        >
                                            {members.map((member: any) => (
                                                <SortableMemberRow
                                                    key={member.id}
                                                    member={member}
                                                    onRemove={
                                                        handleRemoveMember
                                                    }
                                                    onToggleEventPermission={
                                                        handleToggleEventPermission
                                                    }
                                                    onViewRunestone={(
                                                        imageUrl,
                                                        playerName,
                                                    ) =>
                                                        setRuneModal({
                                                            open: true,
                                                            imageUrl,
                                                            playerName,
                                                        })
                                                    }
                                                />
                                            ))}
                                        </SortableContext>
                                    </tbody>
                                </table>
                            </div>
                        </DndContext>
                    )}
                </div>
            )}

            {/* Academy Members Tab */}
            {activeTab === "academy" && (
                <div className="rounded-t-none rounded-b-2xl border border-slate-300 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    {academyMembersLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                        </div>
                    ) : academyMembers.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-slate-600 dark:text-slate-400">
                                No academy members yet
                            </p>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleAcademyDragEnd}
                        >
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Player
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Twitch
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Reward
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Total RP
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Joined
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Display Order
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Runestone
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold tracking-wider text-slate-700 uppercase dark:text-slate-300">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                        <SortableContext
                                            items={academyMembers.map(
                                                (m) => m.id,
                                            )}
                                            strategy={
                                                verticalListSortingStrategy
                                            }
                                        >
                                            {academyMembers.map(
                                                (member: any) => (
                                                    <SortableAcademyMemberRow
                                                        key={member.id}
                                                        member={member}
                                                        onRemove={
                                                            handleRemoveMember
                                                        }
                                                        onPromote={
                                                            handlePromoteAcademyMember
                                                        }
                                                        onViewRunestone={(
                                                            imageUrl,
                                                            playerName,
                                                        ) =>
                                                            setRuneModal({
                                                                open: true,
                                                                imageUrl,
                                                                playerName,
                                                            })
                                                        }
                                                    />
                                                ),
                                            )}
                                        </SortableContext>
                                    </tbody>
                                </table>
                            </div>
                        </DndContext>
                    )}
                </div>
            )}

            {/* Requests Tab */}
            {activeTab === "requests" && (
                <div className="space-y-4">
                    {requestsLoading ? (
                        <div className="flex items-center justify-center rounded-2xl border border-slate-300 bg-white py-12 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                            <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                            <p className="text-slate-600 dark:text-slate-400">
                                No pending requests
                            </p>
                        </div>
                    ) : (
                        requests.map((request: any) => (
                            <div
                                key={request.id}
                                className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                            >
                                <div className="mb-4 flex items-start justify-between">
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                                            {request.player.name}
                                        </h3>
                                        <p className="mt-1 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                            <Clock className="h-4 w-4" />
                                            {new Date(
                                                request.createdAt,
                                            ).toLocaleDateString()}{" "}
                                            at{" "}
                                            {new Date(
                                                request.createdAt,
                                            ).toLocaleTimeString()}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-semibold ${
                                                request.isAcademyApplication
                                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                                    : "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                                            }`}
                                        >
                                            {request.isAcademyApplication && (
                                                <GraduationCap className="h-4 w-4" />
                                            )}
                                            {request.isAcademyApplication
                                                ? "VRX Academy"
                                                : "RAVENGUARD"}
                                        </span>
                                        <span className="inline-flex items-center rounded-full bg-yellow-100 px-4 py-1.5 text-sm font-semibold text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                            Pending
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                                    <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        Message:
                                    </p>
                                    <p className="text-slate-600 dark:text-slate-400">
                                        {request.message}
                                    </p>
                                </div>

                                {rejectingRequestId === request.id ? (
                                    <div className="space-y-3">
                                        <textarea
                                            value={rejectReason}
                                            onChange={(e) =>
                                                setRejectReason(e.target.value)
                                            }
                                            placeholder="Optional: Provide a reason for rejection..."
                                            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                            rows={3}
                                        />
                                        <div className="flex gap-3">
                                            <button
                                                onClick={() =>
                                                    handleRejectRequest(
                                                        request.id,
                                                    )
                                                }
                                                className="rounded-lg bg-red-600 px-6 py-2 font-medium text-white transition-colors hover:bg-red-700"
                                            >
                                                Confirm Rejection
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setRejectingRequestId(null);
                                                    setRejectReason("");
                                                }}
                                                className="rounded-lg border border-slate-300 bg-white px-6 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() =>
                                                handleApproveRequest(request.id)
                                            }
                                            className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2 font-medium text-white transition-colors hover:bg-green-700"
                                        >
                                            <CheckCircle className="h-5 w-5" />
                                            {request.isAcademyApplication
                                                ? "Approve to Academy"
                                                : "Approve"}
                                        </button>
                                        {!request.isAcademyApplication && (
                                            <button
                                                onClick={() =>
                                                    handleSendRequestToAcademy(
                                                        request.id,
                                                    )
                                                }
                                                className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                                            >
                                                <GraduationCap className="h-5 w-5" />
                                                Send to Academy
                                            </button>
                                        )}
                                        <button
                                            onClick={() =>
                                                setRejectingRequestId(
                                                    request.id,
                                                )
                                            }
                                            className="flex items-center gap-2 rounded-lg bg-red-600 px-6 py-2 font-medium text-white transition-colors hover:bg-red-700"
                                        >
                                            <XCircle className="h-5 w-5" />
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Invites Tab */}
            {activeTab === "invites" && (
                <div className="space-y-4">
                    {invitesLoading ? (
                        <div className="flex items-center justify-center rounded-2xl border border-slate-300 bg-white py-12 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                            <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
                        </div>
                    ) : invites.length === 0 ? (
                        <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                            <p className="text-slate-600 dark:text-slate-400">
                                No pending invites
                            </p>
                        </div>
                    ) : (
                        invites.map((invite: any) => (
                            <div
                                key={invite.id}
                                className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                            >
                                <div className="mb-4 flex items-start justify-between">
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                                            {invite.player.name}
                                        </h3>
                                        <p className="mt-1 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                            <Mail className="h-4 w-4" />
                                            Invited by {invite.invitedBy.name}
                                        </p>
                                        <p className="mt-1 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                            <Clock className="h-4 w-4" />
                                            {new Date(
                                                invite.createdAt,
                                            ).toLocaleDateString()}{" "}
                                            at{" "}
                                            {new Date(
                                                invite.createdAt,
                                            ).toLocaleTimeString()}
                                        </p>
                                    </div>
                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-4 py-1.5 text-sm font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                        Pending
                                    </span>
                                </div>

                                {invite.message && (
                                    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                                        <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Message:
                                        </p>
                                        <p className="text-slate-600 dark:text-slate-400">
                                            {invite.message}
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={() =>
                                        handleCancelInvite(
                                            invite.id,
                                            invite.player.name,
                                        )
                                    }
                                    className="flex items-center gap-2 rounded-lg bg-red-600 px-6 py-2 font-medium text-white transition-colors hover:bg-red-700"
                                >
                                    <XCircle className="h-5 w-5" />
                                    Cancel Invitation
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-lg rounded-2xl border border-slate-300 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                        <h2 className="mb-6 text-2xl font-bold text-slate-900 dark:text-slate-100">
                            Invite Players to Stream Team
                        </h2>

                        {selectedPlayers.length > 0 && (
                            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                                <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    Selected Players ({selectedPlayers.length}):
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedPlayers.map((player) => (
                                        <span
                                            key={player.id}
                                            className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                        >
                                            {player.name}
                                            <button
                                                onClick={() =>
                                                    setSelectedPlayers(
                                                        selectedPlayers.filter(
                                                            (p) =>
                                                                p.id !==
                                                                player.id,
                                                        ),
                                                    )
                                                }
                                                className="hover:text-blue-900 dark:hover:text-blue-300"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Search for Players
                            </label>
                            <div className="relative">
                                <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    value={playerSearch}
                                    onChange={(e) =>
                                        setPlayerSearch(e.target.value)
                                    }
                                    placeholder="Type player name to add..."
                                    className="w-full rounded-lg border border-slate-300 bg-white py-3 pr-4 pl-10 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>

                            {searchResults.length > 0 && playerSearch && (
                                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800">
                                    {searchResults.map((player: any) => {
                                        const isSelected =
                                            selectedPlayerIds.includes(
                                                player.id,
                                            );
                                        return (
                                            <button
                                                key={player.id}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedPlayers(
                                                            selectedPlayers.filter(
                                                                (p) =>
                                                                    p.id !==
                                                                    player.id,
                                                            ),
                                                        );
                                                    } else {
                                                        setSelectedPlayers([
                                                            ...selectedPlayers,
                                                            {
                                                                id: player.id,
                                                                name: player.name,
                                                                clan: player.clan,
                                                            },
                                                        ]);
                                                    }
                                                }}
                                                className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
                                                    isSelected
                                                        ? "bg-blue-50 dark:bg-blue-900/20"
                                                        : "hover:bg-slate-50 dark:hover:bg-slate-700"
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-slate-900 dark:text-slate-100">
                                                        {player.name}
                                                    </span>
                                                    {isSelected && (
                                                        <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                    )}
                                                </div>
                                                {player.clan && (
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                                        {player.clan.tag}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="mb-6">
                            <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Invitation Message (Optional)
                            </label>
                            <textarea
                                value={inviteMessage}
                                onChange={(e) =>
                                    setInviteMessage(e.target.value)
                                }
                                placeholder="Add a personal message to the invitation..."
                                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                rows={4}
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleInvitePlayer}
                                disabled={selectedPlayers.length === 0}
                                className="bg-brand hover:bg-brand/90 flex-1 rounded-lg px-6 py-3 font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Send{" "}
                                {selectedPlayers.length > 0
                                    ? `${selectedPlayers.length} `
                                    : ""}
                                Invitation
                                {selectedPlayers.length > 1 ? "s" : ""}
                            </button>
                            <button
                                onClick={() => {
                                    setShowInviteModal(false);
                                    setInviteMessage("");
                                    setSelectedPlayers([]);
                                    setPlayerSearch("");
                                }}
                                className="rounded-lg border border-slate-300 bg-white px-6 py-3 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            <LonghouseModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                }}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />

            {/* Runestone Zoom Modal */}
            {runeModal.open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setRuneModal((m) => ({ ...m, open: false }))}
                >
                    <div
                        className="relative max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="mb-4 text-center text-sm font-semibold text-slate-300">
                            {runeModal.playerName}&rsquo;s Runestone
                        </p>
                        <img
                            src={runeModal.imageUrl}
                            alt={`${runeModal.playerName}'s Runestone`}
                            className="h-64 w-64 object-contain"
                        />
                        <button
                            onClick={() =>
                                setRuneModal((m) => ({ ...m, open: false }))
                            }
                            className="mt-4 w-full rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
